import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { Observable, of } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';
import { InternalTag, OutputTag, Tag } from './model';
import { ViewTagsComponent } from './view-tags.component';

@Component({
  selector: 'miq-tags',
  template: `
    <miq-view-tags
      #viewTags
      [tags]="currentTagList"
      [filterTags]="filterTags"
      [canEdit]="canEdit"
      [enableClipboardFeatures]="enableClipboardFeatures"
      [entityId]="entityId"
      [addTagWhenEnter]="addTagWhenEnter"
      (tagAdded)="tagAdded($event)"
      (tagRemoved)="tagRemoved($event)"
      (clipboardCut)="clipboardCut.emit($event)"
      (clipboardPaste)="clipboardPaste.emit($event)"
      (chipClick)="chipClick.emit($event)"
      class="tags-component"
    ></miq-view-tags>
  `,
  styles: ['.tags-component{ width: 100%; }'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class TagsComponent implements OnChanges {
  @ViewChild('viewTags') public viewTagsComponent: ViewTagsComponent;

  @Input() tags: Tag[] = [];
  @Input() canEdit = false;
  @Input() enableClipboardFeatures = false;
  @Input() entityId: number | string;
  @Input() addTagWhenEnter = false;
  @Output() clipboardCut = new EventEmitter<{ tagNames: string[] }>();
  @Output() clipboardPaste = new EventEmitter<{ clipboardTags: string[] }>();
  @Output() chipClick = new EventEmitter<{ tag: InternalTag; event: Event }>();
  /**
   * If provided, the component will call this function and subscribing to the observable when
   * trying to filter the tags. This input will be ignored when allPossibleTags input is provided
   */
  @Input() filterStreamFn: (filter: string) => Observable<string[]>;
  /**
   * If provided, the component will ignore the filter stream fn
   */
  @Input() allPossibleTags: string[];
  /**
   * A function that returns an observable of number when adding a tag
   * The directive will add the tag before executing this function
   * send -1 to execute the rollback function, otherwise send other number to be assigned as an id
   */
  @Input() addTag: (tag: OutputTag) => Observable<number>;
  /**
   * A function that returns an observable of boolean when deleting a tag
   * The directive will remove the tag before executing this function
   * send true when the tag was deleted successfully and false to execute the rollback function
   */
  @Input() deleteTag: (tag: OutputTag) => Observable<boolean>;

  private count = 0;
  private filterFunction: (filter: string) => Observable<string[]>;

  public currentTagList: InternalTag[] = [];
  public filterTags = (tagParam: string) => {
    return this.getFilteredTags(tagParam);
  };

  constructor(private cdRef: ChangeDetectorRef) {}

  ngOnChanges(simpleChanges: SimpleChanges) {
    if (simpleChanges.tags != null) {
      this.currentTagList = this.convertTagToInternalTag(this.tags);
    }

    if (simpleChanges.filterStreamFn != null || simpleChanges.allPossibleTags != null) {
      if (this.allPossibleTags != null) {
        this.filterFunction = this.filterFromAllPossibleValues;
      } else if (typeof this.filterStreamFn === 'function') {
        this.filterFunction = this.filterStreamFn;
      } else {
        this.filterFunction = () => of([]);
      }
    }
  }

  public tagAdded(tag: InternalTag) {
    if (this.canEdit) {
      if (tag.noExistingTag) {
        tag.display = tag.name;
      }
      this.addToCurrentTagList(tag);
      if (this.addTag) {
        this.addTag({ id: tag.id, name: tag.name, noExistingTag: tag.noExistingTag }).subscribe(id => {
          if (id === -1) {
            // Rolling back
            this.removeFromCurrentTagList(tag);
          } else {
            tag.id = id;
          }
        });
      }
    }
  }

  public tagRemoved(tag: InternalTag) {
    if (this.canEdit) {
      this.removeFromCurrentTagList(tag);
      if (this.deleteTag) {
        this.deleteTag(tag).subscribe(deleted => {
          if (!deleted) {
            // Rolling back
            this.addToCurrentTagList(tag);
          }
        });
      }
    }
  }

  private removeFromCurrentTagList(tag: InternalTag) {
    this.currentTagList = this.currentTagList.filter(x => x.internalId !== tag.internalId);
  }

  private addToCurrentTagList(tag: InternalTag) {
    this.currentTagList = [...this.currentTagList, tag];
    this.cdRef.markForCheck();
  }

  /**
   * @param inputTags an array of strings to be converted
   * @output an array of Tags based on the input parameter
   */
  private convertTagToInternalTag(inputTags: Tag[]): InternalTag[] {
    return inputTags.map<InternalTag>(t => this.getNewInternalTag(t.name, t.id));
  }

  private getNewInternalTag(name: string, id: number): InternalTag {
    return {
      name,
      noExistingTag: false,
      display: name,
      id,
      internalId: ++this.count,
    };
  }

  private filterFromAllPossibleValues(filterParam: string): Observable<string[]> {
    const filterValue = filterParam.toLowerCase();
    return of(this.allPossibleTags.filter(tag => tag.toLowerCase().indexOf(filterValue) === 0));
  }

  private checkInputAlreadyExists(tagParam: string): Observable<boolean> {
    return new Observable<boolean>(observer => {
      const exists = this.currentTagList.some(x => x.name === tagParam);
      observer.next(exists);
      observer.complete();
    });
  }

  private getAddTagOption(tagName: string): InternalTag {
    const addTag = this.getNewInternalTag(tagName, -1);
    addTag.noExistingTag = true;
    addTag.display = `${tagName} (New tag)`;
    return addTag;
  }

  private executeFilterFunctionAndMatchExisting(tagParam: string): Observable<InternalTag[]> {
    return this.filterFunction(tagParam).pipe(
      take(1),
      switchMap(filteredTags => {
        const result: InternalTag[] = [];
        let hasExactMatch = false;
        filteredTags.forEach(tag => {
          // If the written input is exact match with the tag, we are not displaying the add new tag option
          hasExactMatch = hasExactMatch ? hasExactMatch : tag === tagParam;
          // If the tag is already added we don't add it to the result
          const tagAlreadyExists = this.currentTagList.some(y => y.name === tag);
          if (!tagAlreadyExists) {
            result.push(this.getNewInternalTag(tag, -1));
          }
        });
        // If doesn't have exact match and it is not already added, we add the add new tag option
        if (!hasExactMatch) {
          result.push(this.getAddTagOption(tagParam));
        }
        return of(result);
      })
    );
  }

  private getFilteredTags(tagParam: string): Observable<InternalTag[]> {
    return this.checkInputAlreadyExists(tagParam).pipe(
      switchMap((paramExists: boolean) => {
        if (!paramExists) {
          return this.executeFilterFunctionAndMatchExisting(tagParam);
        } else {
          return of([]);
        }
      })
    );
  }
}
